import { useEffect, useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import {
  Button,
  Chip,
  Description,
  FieldError,
  Fieldset,
  Form,
  InputGroup,
  Label,
  Tabs,
  TextField,
} from '@heroui/react';
import type { User } from 'firebase/auth';
import { AsyncButton } from '../../../components/ui/async-button';
import { secondaryPageTabClassName } from '../../../components/ui/page-tab-classes';
import { drawerFieldClassName } from '../../../components/ui/side-drawer-classes';
import { StatusCallout } from '../../../components/ui/status-callout';
import {
  changePassword,
  hasGoogleProvider,
  hasPasswordProvider,
} from '../../../lib/firebase';
import { showErrorFromUnknown, showSuccess } from '../../../lib/toast';

type AccountSubTab = 'details' | 'password';

interface AccountSettingsPanelProps {
  authUser: User | null;
  email?: string | null;
}

function validateCurrentPassword(value: string): string | null {
  if (!value) return 'A senha atual é obrigatória';
  return null;
}

function validateNewPassword(value: string): string | null {
  if (!value) return 'A nova senha é obrigatória';
  if (value.length < 6) return 'A senha deve ter pelo menos 6 caracteres';
  return null;
}

function validateConfirmPassword(value: string, newPassword: string): string | null {
  if (!value) return 'Confirmação de senha é obrigatória';
  if (value !== newPassword) return 'As senhas não coincidem';
  return null;
}

export function AccountSettingsPanel({ authUser, email }: AccountSettingsPanelProps) {
  const hasPassword = hasPasswordProvider(authUser);
  const hasGoogle = hasGoogleProvider(authUser);
  const accountEmail = email ?? authUser?.email ?? null;

  const [subTab, setSubTab] = useState<AccountSubTab>('details');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{
    currentPassword?: string;
    newPassword?: string;
    confirmPassword?: string;
  }>({});

  useEffect(() => {
    if (!attemptedSubmit) return;
    setFieldErrors({
      currentPassword: validateCurrentPassword(currentPassword) ?? undefined,
      newPassword: validateNewPassword(newPassword) ?? undefined,
      confirmPassword: validateConfirmPassword(confirmPassword, newPassword) ?? undefined,
    });
  }, [currentPassword, newPassword, confirmPassword, attemptedSubmit]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!authUser || !hasPassword || submitting) return;

    setAttemptedSubmit(true);
    const nextErrors = {
      currentPassword: validateCurrentPassword(currentPassword) ?? undefined,
      newPassword: validateNewPassword(newPassword) ?? undefined,
      confirmPassword: validateConfirmPassword(confirmPassword, newPassword) ?? undefined,
    };
    setFieldErrors(nextErrors);
    if (nextErrors.currentPassword || nextErrors.newPassword || nextErrors.confirmPassword) {
      return;
    }

    if (newPassword === currentPassword) {
      setFieldErrors((prev) => ({
        ...prev,
        newPassword: 'A nova senha deve ser diferente da senha atual',
      }));
      return;
    }

    setSubmitting(true);
    try {
      await changePassword(authUser, currentPassword, newPassword);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setAttemptedSubmit(false);
      setFieldErrors({});
      showSuccess('Senha alterada com sucesso.');
    } catch (err) {
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setAttemptedSubmit(false);
      setFieldErrors({});
      showErrorFromUnknown(err, 'Erro ao alterar senha.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Tabs
      variant="secondary"
      selectedKey={subTab}
      onSelectionChange={(key) => {
        const next = String(key);
        if (next === 'details' || next === 'password') setSubTab(next);
      }}
    >
      <Tabs.ListContainer>
        <Tabs.List aria-label="Seções da conta" className="w-full">
          <Tabs.Tab id="details" className={secondaryPageTabClassName}>
            Dados da conta
            <Tabs.Indicator />
          </Tabs.Tab>
          <Tabs.Tab id="password" className={secondaryPageTabClassName}>
            Alterar senha
            <Tabs.Indicator />
          </Tabs.Tab>
        </Tabs.List>
      </Tabs.ListContainer>

      <Tabs.Panel id="details" className="pt-4">
        <Fieldset>
          <Fieldset.Legend>Dados da conta</Fieldset.Legend>
          <Description>
            Informações de acesso e provedor de autenticação.
          </Description>
          <Fieldset.Group className="gap-5">
            <div className="flex flex-col gap-1">
              <Label>E-mail</Label>
              <p className="text-sm text-foreground">{accountEmail ?? '—'}</p>
              <Description className="text-xs">
                O e-mail de login não pode ser alterado por aqui.
              </Description>
            </div>

            <div className="flex flex-col gap-2">
              <Label>Método de login</Label>
              <div className="flex flex-wrap gap-1.5">
                {hasGoogle ? (
                  <Chip size="sm" variant="soft" color="accent">
                    Google
                  </Chip>
                ) : null}
                {hasPassword ? (
                  <Chip size="sm" variant="soft" color="success">
                    E-mail e senha
                  </Chip>
                ) : null}
                {!hasGoogle && !hasPassword ? (
                  <Chip size="sm" variant="soft">
                    Não identificado
                  </Chip>
                ) : null}
              </div>
            </div>

            {hasGoogle ? (
              <StatusCallout status="info" title="Conta vinculada ao Google">
                {hasPassword
                  ? 'Você também pode entrar com e-mail e senha. A senha da aba “Alterar senha” é da conta Link Park, não a do Google.'
                  : 'Esta conta foi criada com Google. A senha é gerenciada pela sua conta Google — não é possível alterá-la por aqui.'}
              </StatusCallout>
            ) : null}
          </Fieldset.Group>
        </Fieldset>
      </Tabs.Panel>

      <Tabs.Panel id="password" className="pt-4">
        {!hasPassword ? (
          <Fieldset>
            <Fieldset.Legend>Alterar senha</Fieldset.Legend>
            <Description>Opções de senha para esta conta.</Description>
            <Fieldset.Group>
              <StatusCallout status="info" title="Senha gerenciada pelo Google">
                Esta conta não possui senha própria no Link Park. Use o login com Google para
                acessar.
              </StatusCallout>
            </Fieldset.Group>
          </Fieldset>
        ) : (
          <Form onSubmit={handleSubmit}>
            <Fieldset>
              <Fieldset.Legend>Alterar senha</Fieldset.Legend>
              <Description>
                Informe a senha atual e escolha uma nova senha com pelo menos 6 caracteres.
              </Description>
              <Fieldset.Group>
                <PasswordField
                  label="Senha atual"
                  name="currentPassword"
                  value={currentPassword}
                  onChange={setCurrentPassword}
                  show={showCurrent}
                  onToggleShow={() => setShowCurrent((prev) => !prev)}
                  error={attemptedSubmit ? fieldErrors.currentPassword : undefined}
                  autoComplete="current-password"
                />

                <PasswordField
                  label="Nova senha"
                  name="newPassword"
                  value={newPassword}
                  onChange={setNewPassword}
                  show={showNew}
                  onToggleShow={() => setShowNew((prev) => !prev)}
                  error={attemptedSubmit ? fieldErrors.newPassword : undefined}
                  autoComplete="new-password"
                />

                <PasswordField
                  label="Confirmar nova senha"
                  name="confirmPassword"
                  value={confirmPassword}
                  onChange={setConfirmPassword}
                  show={showNew}
                  onToggleShow={() => setShowNew((prev) => !prev)}
                  error={attemptedSubmit ? fieldErrors.confirmPassword : undefined}
                  autoComplete="new-password"
                />
              </Fieldset.Group>
              <Fieldset.Actions>
                <AsyncButton type="submit" variant="primary" isPending={submitting}>
                  Alterar senha
                </AsyncButton>
              </Fieldset.Actions>
            </Fieldset>
          </Form>
        )}
      </Tabs.Panel>
    </Tabs>
  );
}

function PasswordField({
  label,
  name,
  value,
  onChange,
  show,
  onToggleShow,
  error,
  autoComplete,
}: {
  label: string;
  name: string;
  value: string;
  onChange: (value: string) => void;
  show: boolean;
  onToggleShow: () => void;
  error?: string;
  autoComplete: string;
}) {
  return (
    <TextField name={name} fullWidth type={show ? 'text' : 'password'} isInvalid={!!error}>
      <Label>{label}</Label>
      <InputGroup fullWidth>
        <InputGroup.Input
          className={drawerFieldClassName}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          autoComplete={autoComplete}
          placeholder="••••••••"
        />
        <InputGroup.Suffix>
          <Button
            isIconOnly
            size="sm"
            variant="ghost"
            aria-label={show ? 'Ocultar senha' : 'Mostrar senha'}
            onPress={onToggleShow}
          >
            {show ? <EyeOff size={18} /> : <Eye size={18} />}
          </Button>
        </InputGroup.Suffix>
      </InputGroup>
      {error ? <FieldError>{error}</FieldError> : null}
      {!error && name === 'newPassword' ? (
        <Description className="text-xs">Mínimo de 6 caracteres.</Description>
      ) : null}
    </TextField>
  );
}
